import React, { useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useStore } from "@/store/useStore";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Operators from "@/pages/Operators";
import OperatorDetail from "@/pages/OperatorDetail";
import Forecasting from "@/pages/Forecasting";
import Benchmarking from "@/pages/Benchmarking";
import Economics from "@/pages/Economics";
import Analytics from "@/pages/Analytics";
import MapView from "@/pages/MapView";
import ExportPage from "@/pages/ExportPage";
import api from "@/lib/api";

function App() {
  const { 
    setBasinData, 
    setOperators, 
    setCounties,
    setWells,
    setLastUpdate, 
    setIsLoading,
    setError,
    refreshInterval 
  } = useStore();

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const [basinRes, operatorsRes, countiesRes, wellsRes] = await Promise.all([
        api.get('/basin/overview'),
        api.get('/operators'),
        api.get('/counties'),
        api.get('/wells?limit=1000')
      ]);
      
      setBasinData(basinRes.data);
      setOperators(operatorsRes.data);
      setCounties(countiesRes.data);
      setWells(wellsRes.data);
      setLastUpdate(new Date().toISOString());
      setError(null);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data. Retrying...');
    } finally {
      setIsLoading(false);
    }
  }, [setBasinData, setOperators, setCounties, setWells, setLastUpdate, setIsLoading, setError]);

  useEffect(() => {
    fetchData();
    
    // Set up auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
      // Also trigger a backend refresh
      api.post('/refresh').catch(console.error);
    }, refreshInterval);
    
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/operators" element={<Operators />} />
            <Route path="/operators/:id" element={<OperatorDetail />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/forecasting" element={<Forecasting />} />
            <Route path="/benchmarking" element={<Benchmarking />} />
            <Route path="/economics" element={<Economics />} />
            <Route path="/map" element={<MapView />} />
            <Route path="/export" element={<ExportPage />} />
          </Routes>
        </Layout>
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

export default App;
